import React, { useState, useEffect } from 'react';
import {
  Box, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Typography, Alert, Chip, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Stack,
  ToggleButtonGroup, ToggleButton, TablePagination
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { timesheetService, userService, projectService } from '../services/api';
import { format, startOfWeek, endOfWeek, parseISO, startOfMonth, endOfMonth, isWithinInterval } from 'date-fns';
import { usePagination } from '../hooks/usePagination';

// One group's table — pulled out into its own component (rather than inline in the .map() below)
// so usePagination's hook call gets one consistent instance per group, satisfying the rules of
// hooks; each group also gets its own independent page/rowsPerPage state, which is the right
// behavior since expanding/collapsing or re-filtering one group shouldn't move another's page.
const TimesheetGroupTable: React.FC<{
  rows: any[];
  groupBy: 'none' | 'employee' | 'project';
  onApprove: (id: number) => void;
  onReject: (id: number) => void;
  onUnlock: (id: number) => void;
}> = ({ rows, groupBy, onApprove, onReject, onUnlock }) => {
  const { pagedRows, paginationProps } = usePagination(rows);
  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {groupBy !== 'employee' && <TableCell>Employee</TableCell>}
            {groupBy !== 'project' && <TableCell>Project</TableCell>}
            <TableCell>Date</TableCell>
            <TableCell>Task</TableCell>
            <TableCell>Hours</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pagedRows.map((ts) => (
            <TableRow key={ts.id}>
              {groupBy !== 'employee' && <TableCell>{ts.userName}</TableCell>}
              {groupBy !== 'project' && <TableCell>{ts.projectName}</TableCell>}
              <TableCell>{new Date(ts.date).toLocaleDateString()}</TableCell>
              <TableCell>{ts.taskDescription}</TableCell>
              <TableCell>{ts.hours}</TableCell>
              <TableCell>
                <Chip
                  label={ts.status}
                  color={ts.status === 'Approved' ? 'success' : ts.status === 'Rejected' ? 'error' : 'warning'}
                  size="small"
                />
              </TableCell>
              <TableCell>
                {ts.status === 'Submitted' && (
                  <>
                    <Button size="small" color="success" onClick={() => onApprove(ts.id)}>
                      Approve
                    </Button>
                    <Button size="small" color="error" onClick={() => onReject(ts.id)}>
                      Reject
                    </Button>
                  </>
                )}
                {ts.isLocked && (
                  <Button size="small" onClick={() => onUnlock(ts.id)}>
                    Unlock
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {rows.length > 0 && <TablePagination {...paginationProps} />}
    </TableContainer>
  );
};

const TimesheetApproval: React.FC = () => {
  const [timesheets, setTimesheets] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [message, setMessage] = useState('');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  
  // Filter states
  const [filterType, setFilterType] = useState<'all' | 'date' | 'month' | 'week'>('all');
  const [selectedEmployee, setSelectedEmployee] = useState<string>('');
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [groupBy, setGroupBy] = useState<'none' | 'employee' | 'project'>('none');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [selectedWeekStart, setSelectedWeekStart] = useState(
    format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd')
  );

  useEffect(() => {
    loadPendingTimesheets();
    loadUsers();
    loadProjects();
  }, []);

  const loadUsers = async () => {
    try {
      const data = await userService.getUsers();
      // Deactivated employees don't show up in the regular Employee filter here.
      setUsers((data || []).filter((u: any) => u.isActive));
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadProjects = async () => {
    try {
      const data = await projectService.getProjects();
      setProjects(data || []);
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const loadPendingTimesheets = async () => {
    try {
      const data = await timesheetService.getPendingTimesheets();
      setTimesheets(data);
    } catch (error) {
      console.error('Error loading timesheets:', error);
    }
  };

  const handleApprove = async (id: number) => {
    try {
      await timesheetService.approveTimesheet(id);
      setMessage('Timesheet approved successfully');
      loadPendingTimesheets();
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Error approving timesheet');
    }
  };

  const handleReject = async () => {
    if (!selectedId) return;
    try {
      await timesheetService.rejectTimesheet(selectedId, rejectionReason);
      setMessage('Timesheet rejected successfully');
      setRejectOpen(false);
      setRejectionReason('');
      loadPendingTimesheets();
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Error rejecting timesheet');
    }
  };

  const handleUnlock = async (id: number) => {
    try {
      await timesheetService.unlockTimesheet(id);
      setMessage('Timesheet unlocked successfully');
      loadPendingTimesheets();
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Error unlocking timesheet');
    }
  };

  const getFilteredTimesheets = () => {
    let filtered = timesheets;

    // Filter by employee
    if (selectedEmployee) {
      filtered = filtered.filter(ts => ts.userId.toString() === selectedEmployee);
    }

    // Filter by project
    if (selectedProject) {
      filtered = filtered.filter(ts => ts.projectId.toString() === selectedProject);
    }

    // Filter by date
    if (filterType !== 'all') {
      filtered = filtered.filter((ts) => {
        const date = parseISO(ts.date);

        switch (filterType) {
          case 'date':
            if (startDate && endDate) {
              const start = parseISO(startDate);
              const end = parseISO(endDate);
              if (start > end) {
                return true;
              }
              return isWithinInterval(date, { start, end });
            }
            return true;

          case 'month':
            const monthStart = startOfMonth(parseISO(selectedMonth + '-01'));
            const monthEnd = endOfMonth(monthStart);
            return isWithinInterval(date, { start: monthStart, end: monthEnd });

          case 'week':
            const weekStart = startOfWeek(parseISO(selectedWeekStart), { weekStartsOn: 1 });
            const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
            return isWithinInterval(date, { start: weekStart, end: weekEnd });

          default:
            return true;
        }
      });
    }

    return filtered;
  };

  const getGroupedTimesheets = () => {
    const filtered = getFilteredTimesheets();
    
    if (groupBy === 'none') {
      return { 'All Timesheets': filtered };
    }

    const grouped: { [key: string]: any[] } = {};
    
    filtered.forEach(ts => {
      const key = groupBy === 'employee' ? ts.userName : ts.projectName;
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(ts);
    });

    return grouped;
  };

  const groupedTimesheets = getGroupedTimesheets();

  const toggleGroup = (groupName: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupName)) {
      newExpanded.delete(groupName);
    } else {
      newExpanded.add(groupName);
    }
    setExpandedGroups(newExpanded);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Pending Timesheets
      </Typography>

      {message && <Alert severity="info" sx={{ mb: 2 }}>{message}</Alert>}

      {/* Filter Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterListIcon color="action" />
            <Typography variant="subtitle2">Filter Timesheets</Typography>
          </Box>
          
          <Stack direction="row" spacing={2}>
            <TextField
              select
              label="Employee"
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              size="small"
              sx={{ minWidth: 180 }}
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
            >
              <option value="">All Employees</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>{user.fullName}</option>
              ))}
            </TextField>
            
            <TextField
              select
              label="Project"
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              size="small"
              sx={{ minWidth: 180 }}
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
            >
              <option value="">All Projects</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </TextField>

            <TextField
              select
              label="Group By"
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value as any)}
              size="small"
              sx={{ minWidth: 140 }}
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
            >
              <option value="none">No Grouping</option>
              <option value="employee">Employee</option>
              <option value="project">Project</option>
            </TextField>
          </Stack>

          <ToggleButtonGroup
            value={filterType}
            exclusive
            onChange={(e, value) => value && setFilterType(value)}
            size="small"
            fullWidth
          >
            <ToggleButton value="all">All Dates</ToggleButton>
            <ToggleButton value="date">Date Range</ToggleButton>
            <ToggleButton value="month">Month</ToggleButton>
            <ToggleButton value="week">Week</ToggleButton>
          </ToggleButtonGroup>

          {filterType === 'date' && (
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Stack>
          )}

          {filterType === 'month' && (
            <TextField
              fullWidth
              label="Select Month"
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          )}

          {filterType === 'week' && (
            <TextField
              fullWidth
              label="Week Start (Monday)"
              type="date"
              value={selectedWeekStart}
              onChange={(e) => setSelectedWeekStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Select any date - the week will start from that Monday"
              size="small"
            />
          )}

          <Typography variant="caption" color="text.secondary">
            Showing {Object.values(groupedTimesheets).flat().length} of {timesheets.length} timesheets
            {selectedEmployee && ` • Employee: ${users.find(u => u.id.toString() === selectedEmployee)?.fullName}`}
            {selectedProject && ` • Project: ${projects.find(p => p.id.toString() === selectedProject)?.name}`}
            {groupBy !== 'none' && ` • Grouped by ${groupBy}`}
          </Typography>
        </Stack>
      </Paper>

      {Object.entries(groupedTimesheets).map(([groupName, groupTimesheets]) => (
        <Box key={groupName} sx={{ mb: 3 }}>
          {groupBy !== 'none' ? (
            <Paper 
              sx={{ 
                p: 2, 
                mb: 1, 
                cursor: 'pointer', 
                bgcolor: 'primary.light',
                '&:hover': { bgcolor: 'primary.main' },
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
              onClick={() => toggleGroup(groupName)}
            >
              <Typography sx={{ fontSize: 14, color: 'white' }}>
                {expandedGroups.has(groupName) ? '▼' : '▶'}
              </Typography>
              <Typography variant="h6" sx={{ color: 'white', flex: 1 }}>
                {groupName}
              </Typography>
              <Chip 
                label={`${groupTimesheets.length} timesheets`} 
                size="small" 
                sx={{ bgcolor: 'white', fontWeight: 600 }}
              />
              <Chip 
                label={`${groupTimesheets.reduce((sum, ts) => sum + ts.hours, 0)} hours`} 
                size="small" 
                sx={{ bgcolor: 'white', fontWeight: 600 }}
              />
            </Paper>
          ) : null}
          
          {(groupBy === 'none' || expandedGroups.has(groupName)) && (
            <TimesheetGroupTable
              rows={groupTimesheets}
              groupBy={groupBy}
              onApprove={handleApprove}
              onReject={(id) => { setSelectedId(id); setRejectOpen(true); }}
              onUnlock={handleUnlock}
            />
          )}
        </Box>
      ))}

      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)}>
        <DialogTitle>Reject Timesheet</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Rejection Reason"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            margin="normal"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
          <Button onClick={handleReject} variant="contained" color="error">
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TimesheetApproval;
